"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useCallback, useEffect, useState } from "react";
import { IconRenderer } from "./IconRenderer";

export interface TrustItem {
  label?: string;
  iconName?: string;
  imageUrl?: string;
  imageAlt?: string;
  url?: string;
}

const NAVBAR_LINE_COLOR = "#2e1065";
const DEFAULT_STRIP_BG = "#F5F0E8";
const CAROUSEL_INTERVAL_MS = 4000;
const DESKTOP_CARD_WIDTH = 244;
const DESKTOP_CARD_GAP = 12;
const SLIDE_DURATION_MS = 500;

export interface TrustStripProps {
  items?: TrustItem[];
  backgroundColor?: string;
  showLabel?: boolean;
  showTitle?: boolean;
  heading?: string;
  title?: string;
  description?: string;
  /** When "full" (e.g. Custom Widget Max Width = full), section and content use full width. */
  maxWidth?: "default" | "wide" | "full";
  /** Custom height of each trust item box in px. Images scale to fit. Default 82. */
  cardHeight?: number;
}

const defaultLogos = [
  "/images/logo/affiliation-logos/iadvl.svg",
  "/images/logo/ahrs.svg",
  "/images/logo/aahrs.svg",
  "/images/logo/affiliation-logos/untitled-design.svg",
];

/**
 * Trust Strip - Carousel of affiliation logos with optional title
 */
const DEFAULT_CARD_HEIGHT = 82;

export default function TrustStrip({
  items,
  backgroundColor = DEFAULT_STRIP_BG,
  showLabel = true,
  showTitle = true,
  heading,
  title,
  description,
  maxWidth = "default",
  cardHeight = DEFAULT_CARD_HEIGHT,
}: TrustStripProps) {
  const isFullWidth = maxWidth === "full";
  const boxHeight = Math.min(200, Math.max(60, Number(cardHeight) || DEFAULT_CARD_HEIGHT));
  const imageMaxHeight = showLabel ? Math.max(40, boxHeight - 26) : Math.max(40, boxHeight - 8);
  const sectionBg =
    backgroundColor && backgroundColor.trim() !== ""
      ? backgroundColor.trim()
      : DEFAULT_STRIP_BG;
  const contentMaxWidthClass = isFullWidth
    ? "max-w-none w-full"
    : maxWidth === "wide"
      ? "max-w-6xl"
      : "max-w-4xl";
  const sectionMaxWidthClass = isFullWidth
    ? "max-w-none"
    : maxWidth === "wide"
      ? "max-w-6xl"
      : "max-w-4xl";
  const displayTitle =
    (heading && heading.trim()) || (title && title.trim()) || "";
  const showTitleBlock = showTitle !== false && displayTitle !== "";

  const displayItems = items && items.length > 0 ? items : defaultLogos;
  const length = Array.isArray(displayItems) ? displayItems.length : 0;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [suppressTransition, setSuppressTransition] = useState(false);

  const goNext = useCallback(() => {
    if (length <= 1) return;
    setCurrentIndex((i) => Math.min(length, i + 1));
  }, [length]);

  const goPrev = useCallback(() => {
    if (length <= 1) return;
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, [length]);

  useEffect(() => {
    if (length <= 1) return;
    const id = setInterval(goNext, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [length, goNext]);

  useEffect(() => {
    if (length <= 1 || currentIndex < length) return;
    const t = setTimeout(() => {
      setSuppressTransition(true);
      setCurrentIndex(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSuppressTransition(false);
        });
      });
    }, SLIDE_DURATION_MS);
    return () => clearTimeout(t);
  }, [length, currentIndex]);

  const trackItems = length > 0 ? [...displayItems, ...displayItems] : [];

  const imageContainerClass =
    "flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden";
  const imageBaseClass = "h-full w-full max-w-[180px] object-contain object-center";

  const renderItemContent = (item: TrustItem | string, index: number) => {
    if (typeof item === "string") {
      return (
        <div className={imageContainerClass}>
          <Image
            src={item}
            alt={`Affiliation logo ${index + 1}`}
            width={150}
            height={80}
            className={imageBaseClass}
            style={{ maxHeight: imageMaxHeight }}
          />
        </div>
      );
    }
    const { label, iconName, imageUrl, imageAlt, url } = item;
    const altText = imageAlt || label || `Trust item ${index + 1}`;
    return (
      <>
        {imageUrl ? (
          <div className={imageContainerClass}>
            <Image
              src={imageUrl}
              alt={altText}
              width={150}
              height={80}
              className={imageBaseClass}
              style={{ maxHeight: imageMaxHeight }}
            />
          </div>
        ) : iconName ? (
          <IconRenderer
            name={iconName}
            className="size-12 shrink-0 text-violet-950"
          />
        ) : null}
        {showLabel && label && (
          <p className="mt-1 line-clamp-2 text-center text-xs font-medium text-gray-700 md:text-sm">
            {label}
          </p>
        )}
      </>
    );
  };

  const renderSlide = (item: TrustItem | string, index: number) => {
    const content = (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-1 px-2">
        {renderItemContent(item, index)}
      </div>
    );
    const wrapperClass =
      "flex h-full w-full flex-shrink-0 items-center justify-center bg-white";
    if (typeof item !== "string" && item?.url) {
      return (
        <Link
          key={index}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${wrapperClass} transition-opacity hover:opacity-90`}
        >
          {content}
        </Link>
      );
    }
    return (
      <div key={index} className={wrapperClass}>
        {content}
      </div>
    );
  };

  const arrowButtonClass =
    "flex shrink-0 items-center justify-center p-1 text-gray-500 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-40";

  const slideTransition = suppressTransition
    ? "none"
    : `${SLIDE_DURATION_MS}ms ease-out`;

  return (
    <section
      className={`w-full py-4 mx-auto ${sectionMaxWidthClass}`}
      style={{ backgroundColor: sectionBg }}
    >
      <div className={`mx-auto w-full px-4 md:px-6 ${contentMaxWidthClass}`}>
        {showTitleBlock && (
          <div className="mb-4 grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-end"
              style={{
                background: `linear-gradient(to right, transparent, ${NAVBAR_LINE_COLOR})`,
              }}
              aria-hidden
            />
            <h2 className="text-center font-extrabold uppercase tracking-wide text-violet-950 md:text-xl lg:text-2xl">
              {displayTitle}
            </h2>
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-start"
              style={{
                background: `linear-gradient(to right, ${NAVBAR_LINE_COLOR}, transparent)`,
              }}
              aria-hidden
            />
          </div>
        )}

        <div
          className={`trust-strip-outer mx-auto w-full rounded-xl p-3 md:p-4 ${isFullWidth ? "max-w-none" : contentMaxWidthClass}`}
        >
          {length === 0 ? null : (
            <div className="flex items-center gap-2 md:gap-3">
              <button
                type="button"
                onClick={goPrev}
                className={arrowButtonClass}
                aria-label="Previous"
                disabled={length <= 1}
              >
                <svg
                  className="size-8 md:size-10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              {/* Mobile: full-width viewport, one white card at a time, infinite loop */}
              <div className="relative min-w-0 flex-1 overflow-hidden md:hidden">
                <div
                  className="flex"
                  style={{
                    height: boxHeight,
                    transition: `transform ${slideTransition}`,
                    transform: `translateX(-${currentIndex * 100}%)`,
                  }}
                >
                  {trackItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex min-w-full flex-shrink-0"
                      style={{ height: boxHeight }}
                    >
                      {renderSlide(item, index)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop: show as many cards as fit, centered, infinite loop */}
              <div className="relative hidden min-w-0 flex-1 overflow-hidden md:block">
                <div
                  className="flex justify-center"
                  style={{ height: boxHeight }}
                >
                  <div
                    className="flex"
                    style={{
                      transition: `transform ${slideTransition}`,
                      transform: `translateX(-${currentIndex * (DESKTOP_CARD_WIDTH + DESKTOP_CARD_GAP)}px)`,
                      gap: DESKTOP_CARD_GAP,
                    }}
                  >
                    {trackItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex flex-shrink-0 rounded bg-white"
                        style={{
                          width: DESKTOP_CARD_WIDTH,
                          height: boxHeight,
                        }}
                      >
                        {renderSlide(item, index)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={goNext}
                className={arrowButtonClass}
                aria-label="Next"
                disabled={length <= 1}
              >
                <svg
                  className="size-8 md:size-10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>

        {description && description.trim() !== "" && (
          <p className="mt-4 text-center text-sm text-gray-600 md:text-base">
            {description.trim()}
          </p>
        )}
      </div>
    </section>
  );
}
